import { useId } from "react";
import { Check } from "lucide-react";
import type { PreviewComment } from "../api/contract";
import type { UndoState } from "../hooks/useUndo";
import { resultChoice } from "../lib/simpleChat";
import { Spinner } from "./Spinner";

interface OutcomeCardProps {
  /** The finished comments of one batch. */
  batch: PreviewComment[];
  /** Every comment, to tell whether this batch is the newest. */
  comments: PreviewComment[];
  undo: UndoState;
  onUndo: () => void;
  onShow: (comment: PreviewComment) => void;
  onRestore: (comment: PreviewComment) => void;
  onOpenHistory?: () => void;
}

/**
 * What a finished comment batch did, as one card inside Stax's reply to it: a row per comment,
 * one Undo for the batch, and a way into History. A comment Stax didn't do stays in the list,
 * marked, with its reason. A question Stax asks stays in the reply and is answered with the message box.
 */
export function OutcomeCard({ batch, comments, undo, onUndo, onShow, onRestore, onOpenHistory }: OutcomeCardProps) {
  const heading = useId();
  const done = batch.filter((comment) => comment.state === "done");
  const undoFrom = done[0];
  const choice = undoFrom ? resultChoice(undoFrom, comments, undo) : null;
  return (
    <section className="bw-outcome" aria-labelledby={heading}>
      <div className="bw-outcome-head">
        <Check size={18} strokeWidth={2.5} aria-hidden="true" />
        <h3 id={heading}>{done.length === 0 ? "Nothing changed in your draft" : done.length === 1 ? "1 change in your draft" : `${done.length} changes in your draft`}</h3>
        {undoFrom && (
          <button type="button" className="bw-outcome-undo" disabled={undo.phase === "undoing"} aria-busy={undo.phase === "undoing"} onClick={choice === "undo" ? onUndo : () => onRestore(undoFrom)}>
            {undo.phase === "undoing" && <Spinner />}
            {done.length === 1 ? "Undo" : "Undo all"}
          </button>
        )}
      </div>
      <ol>
        {batch.map((comment) => {
          const finished = comment.state === "done";
          return (
            <li key={comment.id}>
              <button type="button" data-done={finished ? "" : undefined} onClick={() => onShow(comment)}>
                <span className="bw-outcome-pin"><span className="bw-sr">Comment </span>{comment.number}<span className="bw-sr">: </span></span>
                <span className="bw-outcome-text">
                  {finished ? (comment.resultNote ?? comment.note) : comment.note}
                  {/* A question waits in the reply above; anything else says why it stayed as it was. */}
                  {!finished && <small>{comment.state === "asked" ? "Waiting for your answer" : comment.resultNote ? `Left as it was: ${comment.resultNote}` : "Left as it was"}</small>}
                </span>
                <span className="bw-outcome-show">Show</span>
              </button>
            </li>
          );
        })}
      </ol>
      {onOpenHistory && <button type="button" className="bw-outcome-history" onClick={onOpenHistory}>See all versions in History</button>}
    </section>
  );
}
